import { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FloatingParticles from "../components/FloatingParticles";
const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";

const DoctorSelfProfile = ({ doctor: propDoctor }) => {
    const [doctor, setDoctor] = useState(propDoctor || null);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        specialization: "",
        experience: "",
        gender: "",
        consultation_fee: "",
        availability_start: "",
        availability_end: "",
        slot_duration: "",
        clinic_name: "",
        pincode: "",
        flat_no: "",
        area: "",
        landmark: "",
        city: "",
        state: "",
        address: "",
        is_active: "",
        category: "",
        clinics: ""
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const storedDoc = propDoctor || JSON.parse(localStorage.getItem("doctor"));
                if (!storedDoc) {
                    setLoading(false);
                    return;
                }
                // Immediately set initial state so profile can open and display all saved fields even if network fails
                setDoctor(storedDoc);
                setFormData({
                    name: storedDoc.name || "",
                    email: storedDoc.email || "",
                    gender: storedDoc.gender || "",
                    specialization: storedDoc.specialization || "",
                    experience: storedDoc.experience || "",
                    consultation_fee: storedDoc.consultation_fee || "",
                    availability_start: storedDoc.availability_start ? String(storedDoc.availability_start).substring(0, 5) : "",
                    availability_end: storedDoc.availability_end ? String(storedDoc.availability_end).substring(0, 5) : "",
                    slot_duration: storedDoc.slot_duration || 30,
                    clinic_name: storedDoc.clinic_name || "",
                    pincode: storedDoc.pincode || "",
                    flat_no: storedDoc.flat_no || "",
                    area: storedDoc.area || "",
                    landmark: storedDoc.landmark || "",
                    city: storedDoc.city || "",
                    state: storedDoc.state || "",
                    address: storedDoc.address || "",
                    is_active: storedDoc.is_active !== undefined ? storedDoc.is_active : 1,
                    category: storedDoc.category || "Doctor",
                    clinics: storedDoc.clinics || ""
                });

                if (storedDoc.id) {
                    const res = await axios.get(`${API}/api/doctors/${storedDoc.id}`);
                    const docData = res.data;
                    setDoctor(docData);
                    setFormData({
                        name: docData.name || "",
                        email: docData.email || "",
                        gender: docData.gender || "",
                        specialization: docData.specialization || "",
                        experience: docData.experience || "",
                        consultation_fee: docData.consultation_fee || "",
                        availability_start: docData.availability_start ? String(docData.availability_start).substring(0, 5) : "",
                        availability_end: docData.availability_end ? String(docData.availability_end).substring(0, 5) : "",
                        slot_duration: docData.slot_duration || 30,
                        clinic_name: docData.clinic_name || "",
                        pincode: docData.pincode || "",
                        flat_no: docData.flat_no || "",
                        area: docData.area || "",
                        landmark: docData.landmark || "",
                        city: docData.city || "",
                        state: docData.state || "",
                        address: docData.address || "",
                        is_active: docData.is_active !== undefined ? docData.is_active : 1,
                        category: docData.category || "Doctor",
                        clinics: docData.clinics || ""
                    });
                }
            } catch (err) {
                console.error("Profile fetch error:", err);
                const fallbackDoc = propDoctor || JSON.parse(localStorage.getItem("doctor"));
                if (!fallbackDoc) {
                    toast.error("Failed to load doctor profile.");
                }
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [propDoctor]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            // Because the PUT route expects FormData if there are files
            const data = new FormData();
            Object.keys(formData).forEach(key => {
                data.append(key, formData[key]);
            });

            const docId = doctor?.id || propDoctor?.id || JSON.parse(localStorage.getItem("doctor"))?.id;
            await axios.put(`${API}/api/doctors/${docId}`, data, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            
            const updatedDoctor = { ...doctor, ...formData };
            setDoctor(updatedDoctor);
            
            const currentStoredDoc = JSON.parse(localStorage.getItem("doctor")) || {};
            localStorage.setItem("doctor", JSON.stringify({ ...currentStoredDoc, ...updatedDoctor, ...formData }));
            window.dispatchEvent(new Event("storage"));
            
            toast.success("Profile updated successfully!");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update profile.");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex justify-center items-center">
                <div className="text-xl font-bold text-blue-600 animate-pulse font-['Outfit']">Loading Profile...</div>
            </div>
        );
    }

    if (!doctor) return <div className="text-center mt-20 text-xl font-['Outfit']">Please login to view profile.</div>;

    return (
        <div className="min-h-[90vh] bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] p-4 lg:p-8 flex justify-center items-start font-['Outfit'] relative overflow-hidden text-slate-800">
            <FloatingParticles />
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="w-full max-w-5xl relative z-10">
                {/* Header Space */}
                <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-extrabold text-[#0c4a6e] tracking-tight">Dr. {doctor.name}</h1>
                        <p className="text-base text-slate-500 font-medium mt-1">{doctor.email}</p>
                    </div>

                    <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl shadow-lg shadow-blue-500/30 transform hover:-translate-y-1 transition-all border border-blue-400 backdrop-blur-md">
                        <span className="text-white text-xl">👨‍⚕️</span>
                        <div>
                            <div className="text-white font-extrabold tracking-wide uppercase text-sm leading-tight">Doctor Profile</div>
                            <div className="text-blue-100 font-medium text-xs">ID: D-{String(doctor.id || "").padStart(6, '0')}</div>
                        </div>
                    </div>
                </div>

                {/* Profile Card */}
                <div className="bg-white/80 backdrop-blur-2xl rounded-[2rem] shadow-2xl shadow-blue-900/10 border border-white/60 overflow-hidden">
                    <div className="px-8 py-6 border-b border-blue-50 bg-white/40">
                        <h2 className="text-xl font-extrabold text-[#0c4a6e]">Professional Details</h2>
                        <p className="text-sm text-slate-500 font-medium mt-1">Keep your professional information and working hours up to date.</p>
                    </div>

                    <form onSubmit={handleUpdate} className="p-8 space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            
                            {/* Personal Details */}
                            <div className="col-span-1 md:col-span-2">
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 border-b pb-2">Personal</h3>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">👤 Full Name</label>
                                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>
                            
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">📧 Email</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">⚧ Gender</label>
                                <select name="gender" value={formData.gender} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700">
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            {/* Professional Details */}
                            <div className="col-span-1 md:col-span-2 mt-2">
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 border-b pb-2">Professional</h3>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🎓 Specialization</label>
                                <input type="text" name="specialization" value={formData.specialization} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">⭐ Experience (Years)</label>
                                <input type="number" name="experience" value={formData.experience} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>
                            
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">💰 Consultation Fee (₹)</label>
                                <input type="number" name="consultation_fee" value={formData.consultation_fee} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            {/* Working Hours */}
                            <div className="col-span-1 md:col-span-2 mt-2">
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 border-b pb-2">Working Hours & Slots</h3>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">⏰ Start Time</label>
                                <input type="time" name="availability_start" value={formData.availability_start} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">⏰ End Time</label>
                                <input type="time" name="availability_end" value={formData.availability_end} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">⏳ Slot Duration (Mins)</label>
                                <select name="slot_duration" value={formData.slot_duration} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700">
                                    <option value="15">15 mins</option>
                                    <option value="30">30 mins</option>
                                    <option value="45">45 mins</option>
                                    <option value="60">60 mins</option>
                                </select>
                            </div>

                            {/* Location Details */}
                            <div className="col-span-1 md:col-span-2 mt-2">
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 border-b pb-2">Location & Address</h3>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🏥 Clinic / Hospital Name</label>
                                <input type="text" name="clinic_name" value={formData.clinic_name} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🔢 Pincode *</label>
                                <input type="text" name="pincode" placeholder="6 digits [0-9] PIN code" value={formData.pincode} onChange={handleChange} required pattern="[0-9]{6}" maxLength={6}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🏢 Flat, House no., Building, Company, Apartment *</label>
                                <input type="text" name="flat_no" placeholder="E.g. Room 204, MedCare Plaza" value={formData.flat_no} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🛣️ Area, Street, Sector, Village *</label>
                                <input type="text" name="area" placeholder="E.g. Jubilee Hills, Road No. 36" value={formData.area} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🚩 Landmark</label>
                                <input type="text" name="landmark" placeholder="E.g. near metro station" value={formData.landmark} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🏙️ Town/City *</label>
                                <input type="text" name="city" placeholder="E.g. Hyderabad" value={formData.city} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium" />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🗺️ State *</label>
                                <select name="state" value={formData.state} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700">
                                    <option value="">Choose a state</option>
                                    {[
                                        "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", 
                                        "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", 
                                        "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
                                        "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", 
                                        "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", 
                                        "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
                                    ].map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>

                            <div className="col-span-1 md:col-span-2 flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">📍 General / Legacy Address Notes</label>
                                <textarea name="address" value={formData.address} onChange={handleChange} rows="2" placeholder="Optional general notes about address directions"
                                    className="w-full px-4 py-3 bg-white border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm resize-none font-medium"></textarea>
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-blue-50 mt-4">
                            <button type="submit"
                                className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-[#0ea5e9] hover:from-blue-700 hover:to-[#0284c7] text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-1 flex items-center gap-2 text-lg">
                                <span>📥</span> Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DoctorSelfProfile;
